/**
 * Session-wide Plyx feed preferences.
 *
 * Right now this stores just one thing: the user's mute preference.
 * Once they unmute a video in the feed, every subsequent video they
 * swipe to should also play unmuted — no more re-tapping the speaker
 * icon on every clip. Survives page reloads via localStorage.
 *
 * Why a module instead of React Context: VideoCard instances mount
 * and unmount independently as the user swipes, and we want the
 * preference to propagate to siblings that are already on screen
 * (autoplay/pre-render) without re-rendering the whole feed. A
 * tiny eventemitter does this in ~10 lines without touching the feed
 * page's render tree.
 */
const KEY = "plyx_feed_muted";
const EVT = "plyx-feed-muted-change";

/** Read the current preference. Defaults to muted (true) so autoplay
 * works on mobile browsers that block sound-on autoplay. */
export const getPlyxFeedMuted = () => {
  try {
    return localStorage.getItem(KEY) !== "false";
  } catch {
    return true;
  }
};

/** Persist + broadcast. All mounted VideoCards listening on `EVT`
 * will pick this up and sync their local `muted` state. */
export const setPlyxFeedMuted = (muted) => {
  try {
    localStorage.setItem(KEY, muted ? "true" : "false");
  } catch { /* private mode / disabled storage — best-effort */ }
  try {
    window.dispatchEvent(new CustomEvent(EVT, { detail: { muted: !!muted } }));
  } catch { /* very old browsers — best-effort */ }
};

/** Subscribe to mute-pref changes. Returns an unsubscribe function. */
export const subscribePlyxFeedMuted = (handler) => {
  const fn = (e) => handler(!!(e?.detail?.muted ?? getPlyxFeedMuted()));
  window.addEventListener(EVT, fn);
  // Also catch cross-tab changes via the storage event.
  const storageFn = (e) => { if (e.key === KEY) handler(getPlyxFeedMuted()); };
  window.addEventListener("storage", storageFn);
  return () => {
    window.removeEventListener(EVT, fn);
    window.removeEventListener("storage", storageFn);
  };
};

// ── Plyx audio signature ─────────────────────────────────────────────
// Every video that becomes the active card in the feed gets a tiny
// (~1s) Plyx audio identity at the start: a 3-note arpeggio followed
// by a softly synthesized "Plyx" voice. Done with WebAudio + the
// browser's SpeechSynthesis API so we don't need to ship an asset.
//
// Gated to once per (session × video) so re-watches aren't spammy,
// and skipped while the feed is muted (browser policy + UX courtesy).

const CHIME_PLAYED_KEY = "plyx_chime_played_session"; // a Set, stored as JSON

const _chimedThisSession = () => {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(CHIME_PLAYED_KEY) || "[]"));
  } catch {
    return new Set();
  }
};
const _markChimed = (videoId) => {
  try {
    const s = _chimedThisSession();
    s.add(videoId);
    sessionStorage.setItem(CHIME_PLAYED_KEY, JSON.stringify(Array.from(s)));
  } catch { /* private mode — best-effort */ }
};

/** Play the Plyx chime for the given video.
 *
 * iter-119g: FULLY DISABLED (Tobechukwu @tobest, plyx.co.uk).
 *
 * Even after removing the speechSynthesis TTS in iter-119f, users
 * reported "the video says one word, then silent" — that "one word"
 * was the 3-note WebAudio arpeggio (A5/C#6/E6) we played when each
 * card became active. On desktop Chrome with low Media Engagement
 * Index for plyx.co.uk, unmuted autoplay is denied → the video
 * falls back to muted playback. So the user hears arpeggio (0.5s)
 * → silence, identical to the iter-119f symptom.
 *
 * The chime was a brand-identity nice-to-have but it was actively
 * sabotaging the perceived audio quality of every clip. Disable it
 * entirely. The Plyx intro/outro chime baked into downloaded videos
 * (via FFmpeg in plyx_intro_asset.py) is unaffected and still ships
 * the brand sound on shared MP4s where it can't compete with autoplay.
 *
 * Kept as a no-op (instead of deleting) so existing call sites in
 * VideoCard.jsx / PlyxFeed don't need to change. */
export const playPlyxChime = (_videoId) => {
  // Intentionally no-op — see comment above.
};

// ── Resume-position memory ──────────────────────────────────────────
// Long videos that the user scrubs back to should pick up where they
// left off, not restart from 0. We keep a small ring of the most
// recent {videoId → seconds} in localStorage, capped so it can't
// grow unbounded.
const PROGRESS_PREFIX = "plyx_progress:";
const PROGRESS_INDEX = "plyx_progress_index"; // ordered list of recent videoIds
const PROGRESS_MAX = 40;

export const getPlyxVideoProgress = (videoId) => {
  try {
    const raw = localStorage.getItem(PROGRESS_PREFIX + videoId);
    if (!raw) return 0;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch { return 0; }
};

export const setPlyxVideoProgress = (videoId, seconds) => {
  if (!videoId || !Number.isFinite(seconds) || seconds < 1) return;
  try {
    localStorage.setItem(PROGRESS_PREFIX + videoId, String(Math.round(seconds * 10) / 10));
    const idx = JSON.parse(localStorage.getItem(PROGRESS_INDEX) || "[]");
    const filtered = idx.filter((id) => id !== videoId);
    filtered.unshift(videoId);
    while (filtered.length > PROGRESS_MAX) {
      const dropId = filtered.pop();
      localStorage.removeItem(PROGRESS_PREFIX + dropId);
    }
    localStorage.setItem(PROGRESS_INDEX, JSON.stringify(filtered));
  } catch { /* storage full / disabled — best-effort */ }
};

export const clearPlyxVideoProgress = (videoId) => {
  try {
    localStorage.removeItem(PROGRESS_PREFIX + videoId);
  } catch { /* ignore */ }
};
